import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';
import { BookmarkCheck, ChevronRight, Loader2, ArrowLeft, Play, Plus, Pencil, Check, X, RefreshCw, Sparkles, Bot as BotIcon, Trash2, AlertTriangle } from 'lucide-react';
import PinGateModal from '../components/PinGateModal';
import AiDisclaimerBanner from '../components/AiDisclaimerBanner';
import { toast } from 'sonner';
import { stripMarkers } from '../lib/textMarkers';

export default function BookOverview() {
  const { bookId } = useParams();
  const nav = useNavigate();
  const { uiLang, user } = useApp();
  // Iter-52.55.3 — admins bypass the "only outdated pages" rescan
  // gating so they can iterate on OCR rules without waiting for
  // the customer-side Update-available chip. Backend also exempts
  // admin accounts from the 3/day rate limit.
  const isAdmin = !!user?.is_admin;
  const t = useT(uiLang);
  const [book, setBook] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  // A queued action that should run after PinGateModal returns a fresh
  // unlock token. Currently only book-rename uses this — page-level
  // edit/re-extract UI were removed on request in iter-39.
  const [pendingAction, setPendingAction] = useState(null);

  // Inline book-title rename state.
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);

  // Iter-51.58 — per-page delete confirmation. Holds the page + its
  // index while the parent confirms; null when no prompt is open.
  const [pageToDelete, setPageToDelete] = useState(null);
  const [deletingPage, setDeletingPage] = useState(false);

  const confirmDeletePage = async () => {
    if (!pageToDelete) return;
    const { page_id, index } = pageToDelete;
    setDeletingPage(true);
    try {
      const { data } = await api.delete(`/books/${book.book_id}/pages/${page_id}`);
      if (data?.book_deleted) {
        toast.success('Page deleted. Book removed (no pages left).');
        nav('/books');
        return;
      }
      setBook(b => b && ({ ...b, pages: (b.pages || []).filter(p => p.page_id !== page_id) }));
      toast.success(`Page ${index + 1} deleted.`);
      setPageToDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete this page.');
    } finally {
      setDeletingPage(false);
    }
  };

  // Iter-51.8: the per-page "Re-scan this page" flow (iter-51.2) has been
  // removed. Batch "Rescan all pages" (below) is now the only way to
  // re-run OCR against a book.

  // Iter-51.7 — Rescan ALL pages sequentially so the server never handles
  // more than one Claude OCR at a time per book. Progress state lives on
  // the page component so the parent can watch it; if they navigate away
  // the loop keeps running (the reference to `alive` is closed over) and
  // fires a completion toast when done.
  const [ocrVersion, setOcrVersion] = useState(null);
  const [batchState, setBatchState] = useState({ running: false, done: 0, total: 0, failed: 0 });
  const batchAliveRef = useRef({ cancelled: false });
  // Iter-52.48 — per-page rescan state so admins can rescan ONE page
  // at a time without kicking off the full-book batch. Holds the
  // page_id currently rescanning (or null) so the tile can show a
  // spinner and other tiles' buttons can be disabled.
  const [singleRescanning, setSingleRescanning] = useState(null);
  useEffect(() => {
    api.get('/config/ocr-version').then(({data}) => setOcrVersion(data.version)).catch(() => {});
  }, []);
  useEffect(() => () => { batchAliveRef.current.cancelled = true; }, []);

  // Iter-52.48 — rescan a single page in place. Uses the same
  // `/books/{book_id}/pages/{page_id}/rescan` endpoint the batch
  // flow uses (it updates the same page_id — never creates a
  // duplicate), so reading history / bookmarks are preserved.
  // Kept separate from `runBatchRescan` so per-page taps don't
  // block or interfere with a batch run.
  // Iter-52.55.2 — confirm dialog + 429 rate-limit friendly toast.
  // Also surfaces `rescans_remaining_today` in the toast so parents
  // know how many taps they have left. Only affects the SINGLE-page
  // rescan path; the batch "Rescan all" flow already has its own
  // confirmation via the top banner.
  const [rescanConfirm, setRescanConfirm] = useState(null); // { page } | null
  const runSinglePageRescan = async (page) => {
    if (singleRescanning || batchState.running) return;
    setSingleRescanning(page.page_id);
    try {
      const { data } = await api.post(`/books/${bookId}/pages/${page.page_id}/rescan`);
      setBook(b => b && ({
        ...b,
        pages: b.pages.map(p => p.page_id === page.page_id
          ? { ...p, text: data.text, ocr_version: data.ocr_version }
          : p),
      }));
      const remain = typeof data.rescans_remaining_today === 'number'
        ? ` (${data.rescans_remaining_today} left today)` : '';
      toast.success(`Page rescanned — text updated in place.${remain}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403 && /pin/i.test(String(detail || ''))) {
        setPendingAction({ kind: 'rescan-single', page });
        setGateOpen(true);
        return;
      }
      if (status === 429) {
        toast.error(
          detail || 'Aaj ke liye rescan limit poori ho gayi (3/day). Kal fir se try karein.'
        );
        return;
      }
      toast.error(detail || 'Could not rescan this page.');
    } finally {
      setSingleRescanning(null);
    }
  };

  const runBatchRescan = async (pagesToDo) => {
    if (batchState.running) return;
    batchAliveRef.current = { cancelled: false };
    const alive = batchAliveRef.current;
    setBatchState({ running: true, done: 0, total: pagesToDo.length, failed: 0 });
    let done = 0, failed = 0;
    for (const page of pagesToDo) {
      if (alive.cancelled) return;
      try {
        const { data } = await api.post(`/books/${bookId}/pages/${page.page_id}/rescan`);
        setBook(b => b && ({ ...b, pages: b.pages.map(p => p.page_id === page.page_id ? { ...p, text: data.text, ocr_version: data.ocr_version } : p) }));
      } catch (e) {
        failed += 1;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 403 && /pin/i.test(String(detail || ''))) {
          // First page hit a PIN gate — surface the gate and abort the rest;
          // once unlocked the parent can tap the button again to resume.
          setPendingAction({ kind: 'rescan-all', pages: pagesToDo.slice(done) });
          setGateOpen(true);
          setBatchState({ running: false, done, total: pagesToDo.length, failed });
          return;
        }
        // Non-blocking failure — keep going.
        console.warn('rescan-all page failed', page.page_id, detail || status);
      }
      done += 1;
      if (!alive.cancelled) setBatchState({ running: true, done, total: pagesToDo.length, failed });
    }
    if (alive.cancelled) return;
    setBatchState({ running: false, done, total: pagesToDo.length, failed });
    if (failed === 0) toast.success(`Re-scanned all ${done} page${done === 1 ? '' : 's'}.`);
    else toast.success(`Re-scanned ${done - failed} of ${done}. ${failed} page${failed === 1 ? '' : 's'} failed — try again in a moment.`);
  };
  const cancelBatch = () => {
    batchAliveRef.current.cancelled = true;
    setBatchState(s => ({ ...s, running: false }));
    toast.info('Re-scan cancelled.');
  };

  useEffect(() => { api.get(`/books/${bookId}`).then(({data}) => setBook(data)); }, [bookId]);

  const startRename = () => { setTitleDraft(book.title || ''); setTitleEditing(true); };
  const cancelRename = () => { setTitleEditing(false); setTitleDraft(''); };
  const saveRename = async () => {
    const next = titleDraft.trim();
    if (!next) { toast.error('Title cannot be empty'); return; }
    if (next === book.title) { setTitleEditing(false); return; }
    setTitleSaving(true);
    try {
      await api.patch(`/books/${bookId}`, { title: next });
      setBook(b => ({ ...b, title: next }));
      setTitleEditing(false);
      toast.success('Title updated');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403 && /pin/i.test(String(detail || ''))) {
        setPendingAction({ kind: 'rename', title: next });
        setGateOpen(true);
      } else {
        toast.error(`Rename failed: ${detail || `HTTP ${status || 'network'}`}`);
      }
    } finally { setTitleSaving(false); }
  };

  const onPinUnlocked = () => {
    setGateOpen(false);
    const action = pendingAction;
    setPendingAction(null);
    if (!action) {
      // Default gate use = "Add more pages" flow.
      nav(`/books/${bookId}/add-pages`);
      return;
    }
    if (action.kind === 'rename') {
      api.patch(`/books/${bookId}`, { title: action.title })
        .then(() => { setBook(b => ({ ...b, title: action.title })); toast.success('Title updated'); })
        .catch(e => toast.error(`Rename failed: ${e?.response?.data?.detail || 'error'}`));
    }
    if (action.kind === 'rescan-all') {
      runBatchRescan(action.pages);
    }
    if (action.kind === 'rescan-single') {
      runSinglePageRescan(action.page);
    }
  };

  if (!book) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto"/></div>;

  const cur = Math.min(book.current_page_index || 0, book.pages.length - 1);
  const totalChunksOnCur = book.pages[cur]?.chunks?.length || 1;
  const curChunk = (book.current_chunk_index || 0) + 1;
  const isStart = (book.current_page_index || 0) === 0 && (book.current_chunk_index || 0) === 0;

  return (
    <div className="px-6 pt-6 pb-44 max-w-4xl mx-auto overflow-x-hidden">
      <button onClick={() => nav('/books')} className="pushable bg-white border-2 border-slate-200 font-bold px-4 py-2 rounded-full inline-flex items-center gap-2 text-sm mb-4">
        <ArrowLeft size={16} strokeWidth={3}/> {t('books')}
      </button>

      {/* Book title with inline rename */}
      {!titleEditing ? (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h1 data-testid="book-overview-title" className={`text-3xl font-extrabold ${book.language==='hi'?'font-hi':''}`}>{book.title}</h1>
          <button data-testid="rename-book-btn" onClick={startRename}
            title="Rename book"
            className="pushable bg-white border-2 border-slate-200 hover:border-[#FF6B1A] rounded-full p-2">
            <Pencil size={16} strokeWidth={3} className="text-[#FF6B1A]"/>
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <input
            data-testid="book-title-input"
            autoFocus
            value={titleDraft}
            onChange={e => setTitleDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') cancelRename(); }}
            spellCheck={false}
            maxLength={120}
            className={`text-2xl font-extrabold px-4 py-2 rounded-2xl border-2 border-[#FF6B1A] outline-none flex-1 min-w-[200px] ${book.language==='hi'?'font-hi':''}`}
          />
          <button data-testid="rename-save-btn" onClick={saveRename} disabled={titleSaving}
            className="pushable bg-[#FF6B1A] text-white font-bold px-4 py-2 rounded-full inline-flex items-center gap-1 disabled:opacity-60">
            {titleSaving ? <Loader2 size={16} className="animate-spin"/> : <Check size={16} strokeWidth={3}/>} Save
          </button>
          <button data-testid="rename-cancel-btn" onClick={cancelRename}
            className="pushable bg-white border-2 border-slate-200 text-slate-600 font-bold px-4 py-2 rounded-full inline-flex items-center gap-1">
            <X size={16} strokeWidth={3}/> Cancel
          </button>
        </div>
      )}
      <p className="text-sm font-bold text-slate-500 mb-2">{book.pages.length} pages · {book.language.toUpperCase()}</p>
      <button data-testid="add-more-pages-btn" onClick={() => setGateOpen(true)}
        className="pushable bg-white border-2 border-[#FF6B1A] text-[#FF6B1A] font-bold px-4 py-2 rounded-full inline-flex items-center gap-1 text-sm mb-6">
        <Plus size={16} strokeWidth={3}/> Add more pages
      </button>
      {gateOpen && (
        <PinGateModal
          context={(pendingAction?.kind === 'rescan-all' || pendingAction?.kind === 'rescan-single') ? 'rescan' : 'add-book'}
          onUnlocked={onPinUnlocked}
          onCancel={() => { setGateOpen(false); setPendingAction(null); }}
        />
      )}

      {/* Iter-51.7 — "This book has old-OCR pages" banner + Rescan All button.
          Only shows when the current OCR prompt version is HIGHER than
          any stored page.ocr_version, so parents see this exactly when
          there's a real improvement waiting. */}
      {(() => {
        if (!ocrVersion) return null;
        const outdatedPages = book.pages.filter(p =>
          (p.ocr_version == null || p.ocr_version < ocrVersion) && p.image_base64,
        );
        const outdatedCount = outdatedPages.length;
        if (outdatedCount === 0 && !batchState.running) return null;
        const progressPct = batchState.total > 0
          ? Math.round((batchState.done / batchState.total) * 100)
          : 0;
        return (
          <div data-testid="rescan-banner"
            className="mb-4 rounded-3xl border-2 border-[#5B8DEF]/40 bg-gradient-to-br from-[#EFF6FF] to-white p-4 flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#5B8DEF] text-white flex items-center justify-center flex-shrink-0">
                <Sparkles size={18} strokeWidth={2.8}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-[#1E3A8A]">
                  {batchState.running
                    ? `Re-scanning page ${batchState.done + 1} of ${batchState.total}…`
                    : 'New OCR improvements are ready'}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {batchState.running
                    ? 'You can navigate away — this keeps running in the background. We\'ll notify you when it\'s done.'
                    : `${outdatedCount} page${outdatedCount === 1 ? ' was' : 's were'} scanned with an older OCR version. Re-scan to pick up better heading detection and highlighted-word markers.`}
                </p>
              </div>
            </div>
            {batchState.running ? (
              <>
                <div className="h-2 rounded-full bg-white overflow-hidden">
                  <div data-testid="rescan-progress-bar"
                    className="h-full bg-[#5B8DEF] transition-all duration-300"
                    style={{ width: `${progressPct}%` }}/>
                </div>
                <button data-testid="rescan-all-cancel" onClick={cancelBatch}
                  className="text-sm font-bold text-[#5B8DEF] hover:underline self-start">
                  Cancel
                </button>
              </>
            ) : (
              <button data-testid="rescan-all-btn"
                onClick={() => runBatchRescan(outdatedPages)}
                className="pushable pushable-primary bg-[#5B8DEF] text-white font-extrabold px-5 py-2.5 rounded-full inline-flex items-center gap-2 self-start">
                <RefreshCw size={16} strokeWidth={2.8}/>
                Rescan all {outdatedCount} page{outdatedCount === 1 ? '' : 's'}
              </button>
            )}
          </div>
        );
      })()}

      {!isStart && (
        <div data-testid="bookmark-banner" className="bg-[#F4B740] rounded-3xl p-6 mb-6 flex items-center gap-4 border-2 border-[#FF6B1A]/30">
          <div className="w-14 h-14 rounded-2xl bg-[#FF6B1A] flex items-center justify-center flex-shrink-0">
            <BookmarkCheck size={28} strokeWidth={2.5} className="text-white"/>
          </div>
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-widest text-[#B45309]">{t('bookmark')}</div>
            <div className={`text-lg font-extrabold ${uiLang==='hi'?'font-hi':''}`}>{t('lastReadHere')}</div>
            <div className="text-sm font-bold text-slate-600">
              {t('pageProgress').replace('{p}', cur + 1).replace('{t}', book.pages.length)} · Chunk {curChunk}/{totalChunksOnCur}
            </div>
          </div>
          <button data-testid="continue-from-bookmark-btn" onClick={() => nav(`/read/${book.book_id}`)}
            className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-5 py-3 rounded-full inline-flex items-center gap-2">
            <Play size={18} strokeWidth={3} className="fill-white"/> {t('continueFromHere')}
          </button>
        </div>
      )}
      {isStart && (
        <div className="bg-[#D97398] rounded-3xl p-6 mb-6 flex items-center gap-4">
          <div className="flex-1">
            <div className={`text-lg font-extrabold ${uiLang==='hi'?'font-hi':''}`}>{t('startBook')}</div>
            <div className="text-sm text-slate-600">Fresh book — start from page 1</div>
          </div>
          <button data-testid="start-book-btn" onClick={() => nav(`/read/${book.book_id}`)}
            className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-5 py-3 rounded-full inline-flex items-center gap-2">
            <Play size={18} strokeWidth={3} className="fill-white"/> {t('startReading')}
          </button>
        </div>
      )}

      <h2 className="text-lg font-extrabold mb-3">All pages</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {book.pages.map((p, i) => {
          const isCurrent = i === cur && !isStart;
          const isCompleted = i < cur;
          // Iter-52.48 — per-page rescan gating. Only show the
          // rescan icon when we have a stored image to re-run OCR
          // against; a small "OLD" tag surfaces pages whose stored
          // ocr_version is behind the current one so admins can
          // spot them at a glance even if the top banner is
          // scrolled off / hidden by a stale desktop bundle.
          const isPageOutdated = ocrVersion != null
            && (p.ocr_version == null || p.ocr_version < ocrVersion);
          const canRescanPage = !!p.image_base64;
          const rescanningThis = singleRescanning === p.page_id;
          return (
            <div key={p.page_id} data-testid={`overview-page-${i}`}
              className={`relative bg-white rounded-3xl p-3 text-left border-2 overflow-hidden ${isCurrent ? 'border-[#FF6B1A]' : 'border-transparent'}`}>
              {isCurrent && (
                <div data-testid={`bookmark-ribbon-${i}`}
                  className="absolute top-0 left-0 bg-[#FF6B1A] text-white px-3 py-1 rounded-br-2xl inline-flex items-center gap-1 z-10 shadow-sm">
                  <BookmarkCheck size={12} strokeWidth={3}/>
                  <span className="text-[10px] font-extrabold tracking-wider uppercase">Here</span>
                </div>
              )}
              {isCompleted && (
                <div className="absolute top-2 right-2 bg-[#1E9E6C] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-extrabold z-10">✓</div>
              )}
              {/* Iter-52.55.2 — friendlier tag replacing the terse
                  "OLD" chip. Only shown when the page's stored OCR
                  is behind the server version — same gating that
                  drives the rescan button below. */}
              {isPageOutdated && !isCompleted && (
                <div data-testid={`page-outdated-tag-${i}`}
                  className="absolute top-2 right-2 bg-[#5B8DEF] text-white rounded-full px-2 py-0.5 text-[10px] font-extrabold z-10 shadow-sm inline-flex items-center gap-1"
                  title="A newer OCR version is available — tap the rescan button to update.">
                  <Sparkles size={10} strokeWidth={3}/>
                  <span>Update available</span>
                </div>
              )}
              <button onClick={() => nav(`/read/${book.book_id}/${p.page_id}`)}
                className="block w-full text-left pushable">
                <img src={p.image_base64} alt="" className="w-full h-40 object-cover rounded-2xl mb-2 border border-slate-100"/>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-slate-500">Page {i + 1}</div>
                  <ChevronRight size={16} className="text-slate-400"/>
                </div>
                <p className={`text-xs text-slate-600 line-clamp-2 mt-1 ${book.language==='hi'?'font-hi':''}`}>{stripMarkers(p.text || '').slice(0, 60)}</p>
              </button>
              {/* Iter-51.21 — per-page Beat the Bot shortcut. Passes the
                  page's OCR text as the passage source so the child can
                  quiz on any lesson already in the book, not just what
                  they most recently read. */}
              {(p.text || '').trim().length >= 40 && (
                <button
                  data-testid={`beat-the-bot-page-${i}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const text = stripMarkers(p.text || '').trim();
                    // Iter-51.22 — pass all pages so scope selector on
                    // the Quiz screen can build last-2 / last-3 / whole-book.
                    const allPages = (book.pages || []).map(pp => ({
                      page_id: pp.page_id,
                      text: stripMarkers(pp.text || '').trim(),
                    }));
                    nav(`/quiz/${book.book_id}`, {
                      state: {
                        text,
                        language: book.language || 'en',
                        cacheKey: `${book.book_id}:page:${p.page_id}`,
                        pageIdx: i,
                        chunkIdx: 0,
                        pages: allPages,
                        currentChunkText: text,
                        bookTitle: book.title || '',
                      },
                    });
                  }}
                  className="mt-2 w-full bg-[#1E9E6C] text-white font-bold text-[11px] py-1.5 rounded-full inline-flex items-center justify-center gap-1"
                >
                  <BotIcon size={12} strokeWidth={3}/> Beat the Bot
                </button>
              )}
              {/* Iter-52.55.2 — Rescan button is now only shown on
                  pages whose stored OCR is behind the server's
                  current version. After a successful rescan the
                  ocr_version is updated in-place and the button
                  auto-hides. Prevents kids from tapping repeatedly
                  and burning OCR credits. Also routes through a
                  bilingual confirm dialog. */}
              {canRescanPage && (isPageOutdated || isAdmin) && (
                <button
                  data-testid={`rescan-page-${i}`}
                  aria-label={`Rescan page ${i + 1}`}
                  disabled={rescanningThis || batchState.running || !!singleRescanning}
                  onClick={(e) => { e.stopPropagation(); setRescanConfirm({ page: p, index: i }); }}
                  title={isAdmin && !isPageOutdated
                    ? 'Admin: rescan even though this page is up-to-date'
                    : 'Re-run OCR on this page (updates in place, keeps reading history)'}
                  className={`absolute bottom-2 right-11 w-7 h-7 rounded-full bg-white/95 hover:bg-[#5B8DEF] hover:text-white border inline-flex items-center justify-center shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isPageOutdated ? 'text-[#5B8DEF] border-[#5B8DEF]/60' : 'text-slate-500 border-slate-200'}`}
                >
                  {rescanningThis ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13} strokeWidth={2.5}/>}
                </button>
              )}
              {/* Iter-51.58 — per-page delete. Small, muted trash icon so
                  it doesn't compete with the Beat-the-Bot CTA. Opens a
                  confirmation modal — never deletes on the first tap. */}
              <button
                data-testid={`delete-page-${i}`}
                aria-label={`Delete page ${i + 1}`}
                onClick={(e) => { e.stopPropagation(); setPageToDelete({ page_id: p.page_id, index: i }); }}
                className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-white/95 hover:bg-rose-500 hover:text-white text-slate-500 border border-slate-200 inline-flex items-center justify-center shadow-sm transition-colors"
              >
                <Trash2 size={13} strokeWidth={2.5}/>
              </button>
            </div>
          );
        })}
      </div>

      {/* Iter-52.55.2 — Bilingual rescan confirm modal so kids don't
          fire OCR credits by mis-tapping. Same pattern as the delete
          modal below. */}
      {rescanConfirm && (
        <div data-testid="rescan-confirm-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !singleRescanning && setRescanConfirm(null)}>
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-2xl bg-blue-100 flex items-center justify-center">
                <RefreshCw size={22} className="text-blue-600" strokeWidth={2.5}/>
              </div>
              <div className="text-lg font-extrabold text-[#2B2140]">Rescan this page?</div>
            </div>
            <p className="text-sm text-slate-700 font-bold leading-relaxed mb-1">
              Kya aap sach mein ye page dobara scan karna chahte hain?
            </p>
            <p className="text-sm text-slate-500 italic leading-relaxed mb-1">
              क्या आप सच में यह पेज दोबारा स्कैन करना चाहते हैं?
            </p>
            <p className="text-xs text-slate-500 italic leading-relaxed mb-5">
              Rescans are limited to 3 per day.
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button data-testid="rescan-confirm-cancel" disabled={!!singleRescanning}
                onClick={() => setRescanConfirm(null)}
                className="pushable bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-4 py-2 rounded-full text-sm disabled:opacity-50">
                Cancel
              </button>
              <button data-testid="rescan-confirm-btn" disabled={!!singleRescanning}
                onClick={async () => {
                  const target = rescanConfirm.page;
                  setRescanConfirm(null);
                  await runSinglePageRescan(target);
                }}
                className="pushable bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50">
                {singleRescanning ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14} strokeWidth={2.8}/>}
                Rescan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter-51.58 — per-page delete confirmation modal. */}
      {pageToDelete && (
        <div data-testid="delete-page-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !deletingPage && setPageToDelete(null)}>
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-2xl bg-rose-100 flex items-center justify-center">
                <AlertTriangle size={22} className="text-rose-600" strokeWidth={2.5}/>
              </div>
              <div className="text-lg font-extrabold text-[#2B2140]">Delete this page?</div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              Are you sure you want to delete <span className="font-extrabold">Page {pageToDelete.index + 1}</span>? This can't be undone. The rest of the book stays intact.
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button data-testid="delete-page-cancel" disabled={deletingPage}
                onClick={() => setPageToDelete(null)}
                className="pushable bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-4 py-2 rounded-full text-sm disabled:opacity-50">
                Cancel
              </button>
              <button data-testid="delete-page-confirm" disabled={deletingPage}
                onClick={confirmDeletePage}
                className="pushable bg-rose-500 hover:bg-rose-600 text-white font-extrabold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50">
                {deletingPage ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14} strokeWidth={2.8}/>}
                Delete page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter-52.1 — Permanent AI-disclaimer at the bottom (above the
          BottomNav). Rendered last so DOM order matches visual order. */}
      <AiDisclaimerBanner language={book.language || 'en'} testid="book-overview-ai-disclaimer" />
    </div>
  );
}
